import React, { Component } from 'react';
import {
  Button,
  Row,
  Col,
  Form,
  Card,
  Popover,
  OverlayTrigger,
  ListGroup,
} from 'react-bootstrap';
import { Formik, Field } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Layout from './DashboardLayout';
import Loading from '../components/Loader';
import WorksessionRequestsPDF from '../components/timesheet/WorksessionRequestsPDF';
import WorksessionRequestsExcel from '../components/timesheet/WorksessionRequestsExcel';
import {
  TeamMembersDropdown,
  DateRangePickerField,
  NoDataAlert,
  MemberMultiSelectDropdown,
  MultiSelectItemDropdown,
} from '../components/common';
import { teamService, workSessionService } from '../services';

import { getUserData, msToHMS } from '../helpers';
import {
  MEMBER_VALIDATION_LIMIT,
  DATE_RANGE_OPTIONS,
  NO_DATA,
  UPDATE_FAILURE_ERROR,
  PAGINATION_LIMIT,
} from '../constants';
import Pagination from '../components/common/Pagination';
import {
  WorkSessionApprovalToggle,
  WorkSessionEditOnApprovalModal,
} from '../components/timesheet/index';
import {
  fetchWsRequests,
  fetchDataStart,
  fetchDataCompleted,
} from '../actions/worksession';
import { fetchPendingRequests } from '../actions/requests';
import { fetchNotificationAll } from '../actions/notification';

const REQUEST_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approve', label: 'Approve' },
  { value: 'reject', label: 'Reject' },
];

class WorksessionEditRequests extends Component {
  state = {
    isLoading: false,
    isUpdatingTeamsList: false,
    teamMembersList: [],
    teamMember: [],
    projectsList: [],
    projectId: [],
    status: [],
    startDate: moment().startOf('week').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    daysLimit: 90,
    editOnApprovalDisplay: false,
    selectedRequest: {},
    allBreaksList: [],
    currentPage: 1,
  };

  pageHeading = {
    preTitle: 'Timesheet',
    title: 'Worksession Edit Requests',
  };

  componentDidMount() {
    const teamStrength = getUserData('maxMembersAllowed') || 0;
    this.getTeamsData();
    if (teamStrength <= MEMBER_VALIDATION_LIMIT) {
      this.getWorksessionChangeRequests();
    }
    this.handleGetAllBreakTypes();
  }

  handleDateSelect = (event, picker) => {
    this.setState((state) => ({
      ...state,
      startDate: moment(picker.startDate).format('YYYY-MM-DD'),
      endDate: moment(picker.endDate).format('YYYY-MM-DD'),
    }));
  };

  getWorksessionChangeRequests = (values) => {
    const { getWsChangeRequests } = this.props;
    const {
      startDate,
      endDate,
      teamMember: stateTeamMember,
      projectId: stateProjectId,
      status: stateStatus,
    } = this.state;
    /* prefer the just-submitted form values; fall back to state for the
       refresh calls that pass nothing */
    const teamMember = values?.teamMember ?? stateTeamMember;
    const projectId = values?.projectId ?? stateProjectId;
    const status = values?.status ?? stateStatus;
    this.setState({ currentPage: 1 });
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      fromDate: startDate.concat(' 00:00:00'),
      toDate: endDate.concat(' 23:59:59'),
      includeBreakDetails: true,
      userId: teamMember,
      projectId,
      status,
    };
    if ((teamMember && teamMember.length === 0) || teamMember.includes('all')) {
      delete params.userId;
    }
    if ((projectId && projectId.length === 0) || projectId.includes('all')) {
      delete params.projectId;
    }
    if ((status && status.length === 0) || status.includes('all')) {
      delete params.status;
    }
    getWsChangeRequests(params);
  };

  handleWorkSessionEditRequest = (wsData) => {
    const {
      fetchWsDataStart,
      fetchWsDataCompleted,
      reFetchNotifications,
      getPendingRequests,
    } = this.props;
    fetchWsDataStart();
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      workSessionId: wsData.workSessionId,
      requestId: wsData.requestId,
      status: wsData.status,
    };
    if (wsData.updateRemark && wsData.updateRemark !== '') {
      params.updateRemark = wsData.updateRemark;
    }
    if (wsData.status === 'approve' && wsData.workSessionDetails) {
      params.workSessionDetails = { ...wsData.workSessionDetails };
    }
    workSessionService
      .process_edit_request(params)
      .then((response) => {
        if (response.status) {
          this.getWorksessionChangeRequests();
          getPendingRequests({
            teamOwnerId: localStorage.getItem('teamOwnerId'),
          });
          // Refetch notifications
          reFetchNotifications();
          this.handleEditOnApprovalModal(false);
          toast.success('Work session edit request updated successfully.');
        }
      })
      .catch((error) => {
        if (error.message === 'Validation error') {
          if (Array.isArray(error.data)) {
            error.data.map((item) => toast.error(item.msg));
          } else {
            toast.error(error.data);
          }
        } else if (error.message) {
          toast.error(error.message);
        } else {
          toast.error(UPDATE_FAILURE_ERROR);
        }
        fetchWsDataCompleted();
      });
  };

  getTeamsData = () => {
    this.setState((state) => ({
      ...state,
      isUpdatingTeamsList: true,
    }));
    teamService
      .get_team_members_and_projects({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        summaryList: true,
      })
      .then((response) => {
        const { teamMembers, projects } = response.data;
        this.setState((state) => ({
          ...state,
          teamMembersList: [...(teamMembers || [])],
          projectsList: [...(projects || [])],
          isUpdatingTeamsList: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isUpdatingTeamsList: false,
        }));
      });
  };

  handleEditOnApprovalModal = (displayState) => {
    this.setState((state) => ({
      ...state,
      editOnApprovalDisplay: displayState,
    }));
  };

  handleSelectedRequest = (request) => {
    this.setState((state) => ({
      ...state,
      selectedRequest: { ...request },
    }));
  };

  handleGetAllBreakTypes = async () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    workSessionService
      .get_breaks_list({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
      })
      .then((response) => {
        if (response.data) {
          this.setState((state) => ({
            ...state,
            allBreaksList: [...response.data],
          }));
        }
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  render() {
    const {
      isLoading,
      teamMembersList,
      isUpdatingTeamsList,
      startDate,
      endDate,
      daysLimit,
      editOnApprovalDisplay,
      selectedRequest,
      allBreaksList,
      currentPage,
      projectsList,
    } = this.state;

    const { wsReqData, permissions, features } = this.props;

    const isReviewEnabled = Boolean(
      features && features.isWorkSessionEditReviewEnabled
    );
    const canReview = Boolean(
      permissions && permissions.includes('work_session_request_review')
    );
    const canApprove = Boolean(
      permissions && permissions.includes('work_session_request_approval')
    );

    const teamStrength = getUserData('maxMembersAllowed') || 0;
    // Validate form only if not a member and for accounts which has large size than specified limit
    const isValidateForm = teamStrength > MEMBER_VALIDATION_LIMIT;
    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            teamMember: isValidateForm ? '' : [],
            projectId: [],
            fromDate: startDate,
            toDate: endDate,
            status: [],
          }}
          onSubmit={(values) => {
            this.setState((state) => ({
              ...state,
              teamMember: values.teamMember ? values.teamMember : [],
              projectId: values.projectId ? values.projectId : [],
              status: values.status ? values.status : 'all',
            }));
            this.getWorksessionChangeRequests(values);
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Row>
                <Col md={4} lg={3}>
                  {isValidateForm ? (
                    <TeamMembersDropdown
                      teamMembersList={teamMembersList}
                      isUpdatingTeamsList={isUpdatingTeamsList}
                      isAllOptionRequired={false}
                      isValidate
                      isRequired
                      defaultPlaceholder="Select a Team Member"
                    />
                  ) : (
                    <MemberMultiSelectDropdown
                      id="selectInputRef"
                      name="teamMember"
                      labelText="Team Member(s)"
                      fromArray={teamMembersList}
                      formProps={formikprops}
                      isDisabled={teamMembersList.length < 1 && true}
                      innerRef={this.selectInputRef}
                      isAllTeamMemberOption
                      isAllTeamMemberValuesWithNoArray
                      requiredValueAs="teamMember"
                      showErrorMessage
                    />
                  )}
                </Col>
                <Col md={4} lg={3}>
                  <MultiSelectItemDropdown
                    id="projectSelectInputRef"
                    name="projectId"
                    labelText="Project(s)"
                    fromArray={projectsList.map((project) => ({
                      value: project._id,
                      label: project.title,
                    }))}
                    formProps={formikprops}
                    isDisabled={projectsList.length < 1}
                    innerRef={this.projectSelectInputRef}
                    isAllItemOption
                    isAllItemValuesWithNoArray
                    showErrorMessage
                  />
                </Col>
                <Col md={4} lg={3}>
                  <MultiSelectItemDropdown
                    id="statusSelectInputRef"
                    name="status"
                    labelText="Status"
                    fromArray={REQUEST_STATUS_OPTIONS}
                    formProps={formikprops}
                    isDisabled={REQUEST_STATUS_OPTIONS.length < 1}
                    innerRef={this.statusSelectInputRef}
                    isAllItemOption
                    isAllItemValuesWithNoArray
                    showErrorMessage
                  />
                </Col>
                <Col md={4} lg={3}>
                  <DateRangePickerField
                    handleDateSelect={this.handleDateSelect}
                    startFrom="this week"
                    showRangeOptions
                    preDefinedRanges={DATE_RANGE_OPTIONS}
                    handleDateRangeValidation={this.handleDateRangeValidation}
                    isValidate
                    daysLimit={daysLimit}
                  />
                </Col>
                <Col md={4} lg={3}>
                  <Form.Group controlId="generateData">
                    <Form.Label>&nbsp;</Form.Label>
                    <div>
                      {isValidateForm ? (
                        <Button
                          variant="primary"
                          type="Submit"
                          disabled={!(formikprops.isValid && formikprops.dirty)}
                        >
                          Generate
                        </Button>
                      ) : (
                        <Button variant="primary" type="Submit">
                          Generate
                        </Button>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                {permissions &&
                  permissions.includes('report_export') &&
                  wsReqData?.data?.requests?.length > 0 && (
                    <Col xs="auto" className="ms-auto align-self-end pb-3">
                      Download as{' '}
                      <Button
                        variant="white"
                        type="button"
                        size="sm"
                        className="mx-1"
                        onClick={() =>
                          WorksessionRequestsPDF(
                            wsReqData.data.requests,
                            startDate,
                            endDate
                          )
                        }
                      >
                        PDF
                      </Button>
                      <Button
                        variant="white"
                        type="button"
                        size="sm"
                        className="mx-1"
                        onClick={() =>
                          WorksessionRequestsExcel(wsReqData.data.requests)
                        }
                      >
                        Excel
                      </Button>
                    </Col>
                  )}
              </Row>
            </Form>
          )}
        </Formik>

        {wsReqData &&
        wsReqData.data &&
        wsReqData.data.requests &&
        wsReqData.data.requests.length > 0 ? (
          <Row>
            <Col>
              <Card className="px-0">
                <div>
                  <table className="table table-sm table-hover card-table mt-1">
                    <thead>
                      <tr>
                        <th>Team Member</th>
                        <th>Project</th>
                        <th>Original Worksession</th>
                        <th>Updated Worksession</th>
                        <th>Old Breaks</th>
                        <th>Updated Breaks</th>
                        <th>Requested Date</th>
                        <th>Reason</th>
                        <th>&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wsReqData.data.requests
                        .slice(
                          (currentPage - 1) * PAGINATION_LIMIT,
                          currentPage * PAGINATION_LIMIT
                        )
                        .map((request, index) => (
                          <tr key={index}>
                            <td>
                              {`${request.user.firstName} ${
                                request.user.lastName || ''
                              }`}
                            </td>
                            <td>
                              {request.newProject && request.newProject !== null
                                ? request.newProject.title
                                : request.oldProject.title}
                            </td>
                            <td>
                              Duration:{' '}
                              {msToHMS(request.oldWorkSessionSeconds * 1000)}
                              <br />
                              Check-in:{' '}
                              {moment(request.oldCheckInDate).format(
                                'MMM Do, YYYY h:mm:ss A'
                              )}
                              <br />
                              Check-out:{' '}
                              {moment(request.oldCheckOutDate).format(
                                'MMM Do, YYYY h:mm:ss A'
                              )}
                            </td>
                            <td>
                              Duration:{' '}
                              {msToHMS(request.newWorkSessionSeconds * 1000)}
                              <br />
                              Check-in:{' '}
                              {moment(request.newCheckInDate).format(
                                'MMM Do, YYYY h:mm:ss A'
                              )}
                              <br />
                              Check-out:{' '}
                              {moment(request.newCheckOutDate).format(
                                'MMM Do, YYYY h:mm:ss A'
                              )}
                            </td>
                            <td>
                              {request.oldBreak &&
                                request.oldBreak.length > 0 &&
                                request.oldBreak
                                  .slice(0, 2)
                                  .map((obItem, obIndex) => (
                                    <p
                                      className="mb-2"
                                      key={`wsBreak${obIndex}`}
                                    >
                                      {obItem.type !== null
                                        ? obItem.type.name
                                        : 'Other'}
                                      {obItem.type?.shortCode
                                        ? ` (${obItem.type.shortCode})`
                                        : ''}
                                      <br />
                                      {`${moment(obItem.startTime).format(
                                        'h:mm:ss A'
                                      )} to ${moment(obItem.endTime).format(
                                        'h:mm:ss A'
                                      )}`}
                                    </p>
                                  ))}
                              {request.oldBreak &&
                                request.oldBreak.length > 2 && (
                                  <OverlayTrigger
                                    trigger="click"
                                    rootClose
                                    placement="right"
                                    overlay={
                                      <Popover
                                        className="py-0"
                                        style={{ minWidth: '180px' }}
                                      >
                                        <Popover.Content>
                                          <ListGroup variant="flush">
                                            {request.oldBreak.map((bItem) => (
                                              <ListGroup.Item
                                                style={{ padding: '8px 0' }}
                                              >
                                                {bItem.type !== null
                                                  ? bItem.type.name
                                                  : 'Other'}
                                                {bItem.type?.shortCode
                                                  ? ` (${bItem.type.shortCode})`
                                                  : ''}
                                                <br />
                                                {`${moment(
                                                  bItem.startTime
                                                ).format(
                                                  'h:mm:ss A'
                                                )} to ${moment(
                                                  bItem.endTime
                                                ).format('h:mm:ss A')}`}
                                              </ListGroup.Item>
                                            ))}
                                          </ListGroup>
                                        </Popover.Content>
                                      </Popover>
                                    }
                                  >
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="m-0 px-0"
                                    >
                                      View all breaks
                                    </Button>
                                  </OverlayTrigger>
                                )}
                            </td>
                            <td>
                              {request.newBreak &&
                                request.newBreak.length > 0 &&
                                request.newBreak
                                  .slice(0, 2)
                                  .map((nbItem, nbIndex) => (
                                    <p
                                      className="mb-2"
                                      key={`wsBreak${nbIndex}`}
                                    >
                                      {nbItem.type !== null
                                        ? nbItem.type.name
                                        : 'Other'}
                                      {nbItem.type?.shortCode
                                        ? ` (${nbItem.type.shortCode})`
                                        : ''}
                                      <br />
                                      {`${moment(nbItem.startTime).format(
                                        'h:mm:ss A'
                                      )} to ${moment(nbItem.endTime).format(
                                        'h:mm:ss A'
                                      )}`}
                                    </p>
                                  ))}
                              {request.newBreak &&
                                request.newBreak.length > 2 && (
                                  <OverlayTrigger
                                    trigger="click"
                                    rootClose
                                    placement="right"
                                    overlay={
                                      <Popover
                                        className="py-0"
                                        style={{ minWidth: '180px' }}
                                      >
                                        <Popover.Content>
                                          <ListGroup variant="flush">
                                            {request.newBreak.map((bItem) => (
                                              <ListGroup.Item
                                                style={{ padding: '8px 0' }}
                                              >
                                                {bItem.type !== null
                                                  ? bItem.type.name
                                                  : 'Other'}
                                                {bItem.type?.shortCode
                                                  ? ` (${bItem.type.shortCode})`
                                                  : ''}
                                                <br />
                                                {`${moment(
                                                  bItem.startTime
                                                ).format(
                                                  'h:mm:ss A'
                                                )} to ${moment(
                                                  bItem.endTime
                                                ).format('h:mm:ss A')}`}
                                              </ListGroup.Item>
                                            ))}
                                          </ListGroup>
                                        </Popover.Content>
                                      </Popover>
                                    }
                                  >
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="m-0 px-0"
                                    >
                                      View all breaks
                                    </Button>
                                  </OverlayTrigger>
                                )}
                            </td>
                            <td>
                              <p style={{ maxWidth: '200px' }}>
                                {moment(request.requestedDate).format('lll')}
                              </p>
                            </td>

                            <td>
                              <p style={{ maxWidth: '200px' }}>
                                {request.reason}
                              </p>
                            </td>
                            <td>
                              <WorkSessionApprovalToggle
                                status={request.status}
                                wsDetails={request}
                                isReviewEnabled={isReviewEnabled}
                                canReview={canReview}
                                canApprove={canApprove}
                                wsRequestStatusUpdate={
                                  this.handleWorkSessionEditRequest
                                }
                                handleEditOnApprovalModal={
                                  this.handleEditOnApprovalModal
                                }
                                handleSelectedRequest={
                                  this.handleSelectedRequest
                                }
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {editOnApprovalDisplay && (
                    <WorkSessionEditOnApprovalModal
                      wsItem={selectedRequest}
                      editOnApprovalDisplay={editOnApprovalDisplay}
                      handleEditOnApprovalModal={this.handleEditOnApprovalModal}
                      handleWorkSessionEditRequest={
                        this.handleWorkSessionEditRequest
                      }
                      allBreaksList={allBreaksList}
                    />
                  )}
                  {wsReqData.data.requests.length > PAGINATION_LIMIT && (
                    <div className="d-flex justify-content-end px-4 py-3">
                      <Pagination
                        currentPage={currentPage}
                        totalCount={wsReqData.data.requests.length}
                        pageSize={PAGINATION_LIMIT}
                        onPageChange={(page) =>
                          this.setState({ currentPage: page })
                        }
                      />
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        ) : (
          !wsReqData.isLoading && (
            <NoDataAlert content={NO_DATA.WS_CHANGE_REQUESTS} displayBorder />
          )
        )}

        {/* Loader */}
        {(wsReqData.isLoading || isLoading) && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => ({
  wsReqData: state.wsReducer,
  permissions: state.permissionsReducer,
  features: state.featuresReducer,
});

const mapDispatchToProps = () => ({
  getWsChangeRequests: fetchWsRequests,
  fetchWsDataStart: fetchDataStart,
  fetchWsDataCompleted: fetchDataCompleted,
  reFetchNotifications: fetchNotificationAll,
  getPendingRequests: fetchPendingRequests,
});

WorksessionEditRequests.propTypes = {
  wsReqData: PropTypes.object,
  permissions: PropTypes.array,
  features: PropTypes.object,
  getWsChangeRequests: PropTypes.func,
  fetchWsDataStart: PropTypes.func,
  fetchWsDataCompleted: PropTypes.func,
  reFetchNotifications: PropTypes.func,
  getPendingRequests: PropTypes.func,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps()
)(WorksessionEditRequests);
